import { Grid } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useApi } from '../api/Api';
import { useRequest } from '../api/Request';
import { ListQuestionsResponse } from '../api/theSourceApi';
import LoadMoreButton from '../bts/components/LoadMoreButton';
import LoadingPage from '../loading/LoadingPage';
import QuestionCard from './QuestionCard';
import { Question } from '../database/theSource';
import { QuestionStatus } from './constants/QuestionStatus';
import { useCache } from '../api/cache/Cache';
import { GetBatchUsersResponse, User } from '../database/user';
import { useAuth } from '../auth/Auth';

export type TabValue = 'recent' | 'my' | QuestionStatus.Pending;

interface TheSourceTabProps {
    artist: User;
    query: string;
    data?: ListQuestionsResponse;
    setData: (data: ListQuestionsResponse) => void;
}
const TheSourceTab: React.FC<TheSourceTabProps> = ({ artist, query, data, setData }) => {
    const getPostsRequest = useRequest<ListQuestionsResponse>();
    const api = useApi();
    const listQuestionsRequest = useRequest<ListQuestionsResponse>();
    const getUsersBatchRequest = useRequest<GetBatchUsersResponse>();
    const cache = useCache();
    const auth = useAuth();
    const user = auth.user;

    const handleResponse = useCallback(
        (resp: ListQuestionsResponse) => {
            setData({
                questions: resp.questions,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
        },
        [setData]
    );

    const handleResponseLoadMore = useCallback(
        (resp: ListQuestionsResponse) => {
            const newEntries = (data?.questions || []).concat(
                resp.questions.sort((lhs, rhs) =>
                    rhs.createdAt.localeCompare(lhs.createdAt)
                )
            );

            setData({
                questions: newEntries,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
        },
        [data, setData]
    );

    const cacheUsersfromQuestions = useCallback(
        (questions: Question[]) => {
            const uniqueUsernames = Array.from(
                new Set(questions.flatMap((question) => [question.owner]))
            );

            const missingUsernames = uniqueUsernames.filter(
                (username) => !cache.userSummaries.get(username)
            );

            if (missingUsernames.length > 0) {
                getUsersBatchRequest.onStart();
                api.getUsersBatch(missingUsernames)
                    .then((getUsersBatchResponse) => {
                        const fetchedUsers = getUsersBatchResponse.data.users;
                        cache.userSummaries.putMany(fetchedUsers);
                        getUsersBatchRequest.onSuccess();
                    })
                    .catch((err) => {
                        console.error('Fetch users for requests error: ', err);
                        getUsersBatchRequest.onFailure(err);
                    });
            }
        },
        [api, cache.userSummaries, getUsersBatchRequest]
    );

    const getQuestionListFunction = useCallback(
        (startKey?: string) => {
            const fetchQuestionsFunctions = {
                recent: () => api.listQuestionsByIsPrivate('false', startKey),
                [QuestionStatus.Pending]: () =>
                    api.listQuestionsByStatus(QuestionStatus.Pending, true, startKey),
                my: () => api.listQuestionsByOwner(startKey),
            };
            const typedQuery = query as TabValue;
            const fetchQuestions = fetchQuestionsFunctions[typedQuery];

            return fetchQuestions;
        },
        [api, query]
    );

    const onDelete = (i: number) => {
        const newData = data?.questions || [];
        setData({
            questions: [...newData.slice(0, i), ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    const onSave = (i: number, question: Question) => {
        const newData = data?.questions || [];
        setData({
            questions: [...newData.slice(0, i), question, ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    useEffect(() => {
        if (user && !cache.userSummaries.get(user.username)) {
            cache.userSummaries.put(user);
        }

        if (!listQuestionsRequest.isSent()) {
            listQuestionsRequest.onStart();

            const fetchQuestionList = getQuestionListFunction();
            fetchQuestionList()
                .then((resp) => {
                    handleResponse(resp.data);
                    cacheUsersfromQuestions(resp.data.questions);
                    listQuestionsRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('List requests error: ', err);
                    listQuestionsRequest.onFailure(err);
                });
        }
    }, [
        handleResponse,
        getQuestionListFunction,
        listQuestionsRequest,
        api,
        cache.userSummaries,
        user,
        cacheUsersfromQuestions,
    ]);

    const onLoadMore = () => {
        listQuestionsRequest.onStart();
        const fetchQuestionList = getQuestionListFunction(data?.lastEvaluatedKey || '');
        fetchQuestionList()
            .then((resp) => {
                handleResponseLoadMore(resp.data);
                cacheUsersfromQuestions(resp.data.questions);
                listQuestionsRequest.onSuccess(resp.data);
            })
            .catch((err) => {
                console.error('List requests error: ', err);
                listQuestionsRequest.onFailure(err);
            });
    };

    if (!listQuestionsRequest.isSent() || listQuestionsRequest.isLoading()) {
        return <LoadingPage />;
    }

    return (
        <>
            <Grid container>
                {data?.questions.map((question, i) => (
                    <Grid
                        key={question.owner + question.createdAt}
                        item
                        xs={12}
                        sm={6}
                        md={6}
                        padding={2}
                    >
                        <QuestionCard
                            artist={artist}
                            question={question}
                            onDelete={() => onDelete(i)}
                            onSave={(question) => onSave(i, question)}
                        />
                    </Grid>
                ))}
            </Grid>
            <LoadMoreButton
                request={getPostsRequest}
                startKey={data?.lastEvaluatedKey || ''}
                onLoadMore={onLoadMore}
            />
        </>
    );
};
export default TheSourceTab;
